// Pure helpers for reviewed editorial guides (local fixture data, no network, no database).

const ASIN_RE = /^[A-Z0-9]{10}$/;
export const FORBIDDEN_PATTERNS = [
  [/\$\s?\d/, 'dollar price'],
  [/\b\d(\.\d)?\s*(\/|out of)\s*5\b/i, 'numeric rating'],
  [/\bstars?\b/i, 'star rating'],
  [/\bI(?:'ve| have| tested| saw| used)\b/, 'first-person experience'],
  [/\bwe (?:tested|used|measured|benchmarked)\b/i, 'testing claim'],
  [/\bfree (?:delivery|shipping)\b/i, 'delivery claim'],
  [/\bget it by\b/i, 'delivery estimate'],
  [/\bin stock\b/i, 'stock claim'],
  [/\bbest[- ]?sellers?\b/i, 'sales-rank claim'],
];

/** Footprint area in square inches (width × depth), computed from Dell's published dimensions. */
export function footprintArea(model) {
  return Math.round(model.footprint.widthIn * model.footprint.depthIn * 10) / 10;
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];
/** Spell a model count ("three") so headings never claim a wrong number of recommendations. */
export const countWord = (n) => NUMBER_WORDS[n] ?? String(n);

export function depthSpread(models) {
  const depths = models.map((m) => m.footprint.depthIn);
  return Math.round((Math.max(...depths) - Math.min(...depths)) * 100) / 100;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const BUYABLE = ['in_stock', 'limited'];
const escapeRe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const amazonDpUrl = (asin) => `https://www.amazon.com/dp/${asin}`;
const titleMatches = (model, title) => new RegExp(`(^|[^A-Za-z0-9])${escapeRe(model.modelCode)}($|[^A-Za-z0-9])`, 'i').test(title);

/**
 * IDENTITY verification = "this ASIN/URL is this exact model" (human-supplied):
 *   identityStatus "verified"; a 10-character ASIN; productUrl exactly https://www.amazon.com/dp/<ASIN>;
 *   an identity evidence note and report date; and, if the exact listing title was recorded, it contains the model
 *   code (word-boundary match, so S2425HS never matches S2425HSM).
 * It says nothing about who sells it, in which condition, or whether it can be bought.
 */
export function identityState(model) {
  const a = model?.amazon || {};
  const checks = {
    identityStatus: a.identityStatus === 'verified',
    asin: typeof a.asin === 'string' && ASIN_RE.test(a.asin),
    url: typeof a.asin === 'string' && a.productUrl === amazonDpUrl(a.asin),
    identityEvidence: !!a.identityEvidence,
    identityDate: typeof a.identityReportedAt === 'string' && DATE_RE.test(a.identityReportedAt),
    titleMatch: a.modelTitleSeen == null || (!!model?.modelCode && titleMatches(model, a.modelTitleSeen)),
  };
  const missing = Object.entries(checks).filter(([, ok]) => !ok).map(([k]) => k);
  return { verified: missing.length === 0, missing };
}

/**
 * OFFER verification = evidence for one specific purchasable offer, recorded separately per condition:
 *   offerStatus "verified"; condition "New" (the only condition we link to); a seller; US availability "in_stock" or
 *   "limited"; a verification date; an evidence reference. Observed-but-unrecorded offers never count.
 */
export function offerState(model) {
  const a = model?.amazon || {};
  const checks = {
    offerStatus: a.offerStatus === 'verified',
    condition: a.condition === 'New',
    seller: !!a.seller,
    shipsFrom: !!a.shipsFrom,
    availability: BUYABLE.includes(a.availability),
    verifiedAt: typeof a.verifiedAt === 'string' && DATE_RE.test(a.verifiedAt),
    evidenceRef: !!a.evidenceRef,
  };
  const missing = Object.entries(checks).filter(([, ok]) => !ok).map(([k]) => k);
  return { verified: missing.length === 0, missing };
}

/**
 * An Amazon link may be shown only when identity AND a New offer are verified and no editorial acceptance is outstanding
 * (`editorialAcceptance: "required"` marks e.g. a third-party bundle or seller an editor must accept first).
 */
export function amazonLinkState(model) {
  const id = identityState(model);
  const offer = offerState(model);
  const accepted = model?.amazon?.editorialAcceptance !== 'required';
  const show = id.verified && offer.verified && accepted;
  return { show, asin: show ? model.amazon.asin : null, identityVerified: id.verified, offerVerified: offer.verified, acceptanceOutstanding: !accepted, missing: [...id.missing, ...offer.missing, ...(accepted ? [] : ['editorialAcceptance'])] };
}

/** Affiliate buttons are shown only in a published guide: a draft or approved guide never shows them, whatever the evidence. */
export const guideAllowsLinks = (guide) => guide?.status === 'published';

export function amazonHref(model, partnerTag, guide) {
  const state = amazonLinkState(model);
  if (!guideAllowsLinks(guide) || !state.show || !partnerTag) return null; // no fallback tag
  return `https://www.amazon.com/dp/${state.asin}?tag=${encodeURIComponent(partnerTag)}`;
}

function* strings(value, path = '') {
  if (typeof value === 'string') yield [path, value];
  else if (Array.isArray(value)) for (const [i, v] of value.entries()) yield* strings(v, `${path}[${i}]`);
  else if (value && typeof value === 'object') for (const [k, v] of Object.entries(value)) yield* strings(v, `${path}.${k}`);
}

const MUST_HAVE = ['display', 'stand', 'footprint', 'vesa', 'videoInputs', 'usb', 'usbC', 'speakers', 'dell'];

/** Returns a list of problems; empty means the fixture is acceptable for local review. */
export function validateGuide(guide) {
  const errors = [];
  if (!Array.isArray(guide.models) || guide.models.length < 3 || guide.models.length > 5) errors.push('guide needs 3-5 models');
  const ids = new Set();
  const seenAsins = new Map();
  for (const m of guide.models || []) {
    if (ids.has(m.id)) errors.push(`duplicate model id ${m.id}`);
    ids.add(m.id);
    for (const k of MUST_HAVE) if (m[k] === undefined || m[k] === null || m[k] === '') errors.push(`${m.id}: missing ${k}`);
    const f = m.footprint || {};
    for (const k of ['widthIn', 'depthIn', 'heightMinIn', 'heightMaxIn']) if (typeof f[k] !== 'number') errors.push(`${m.id}: footprint.${k} must be a number`);
    if (!/^https:\/\/www\.dell\.com\//.test(m.dell?.url || '')) errors.push(`${m.id}: dell.url must be a dell.com page`);
    if (!m.dell?.retrieved) errors.push(`${m.id}: dell.retrieved missing`);
    if (!(guide.sources || []).some((s) => s.url === m.dell?.url)) errors.push(`${m.id}: Dell page not listed in sources`);
    for (const key of ['price', 'rating', 'reviews', 'reviewCount', 'delivery', 'stockStatus']) if (key in m) errors.push(`${m.id}: forbidden field ${key}`);
    const a = m.amazon || {};
    if (!m.modelCode) errors.push(`${m.id}: modelCode missing`);
    const OFFER_FIELDS = ['condition', 'seller', 'shipsFrom', 'availability', 'availabilityNote', 'verifiedAt', 'evidenceRef'];
    const IDENTITY_FIELDS = ['asin', 'productUrl', 'identityReportedAt', 'identityEvidence', 'modelTitleSeen'];
    if (!['pending', 'verified'].includes(a.identityStatus)) errors.push(`${m.id}: amazon.identityStatus must be pending or verified`);
    if (a.editorialAcceptance != null && !['required', 'accepted'].includes(a.editorialAcceptance)) errors.push(`${m.id}: amazon.editorialAcceptance must be required or accepted`);
    if (!['pending', 'verified', 'rejected'].includes(a.offerStatus)) errors.push(`${m.id}: amazon.offerStatus must be pending, verified or rejected`);
    if (a.identityStatus === 'verified') {
      const st = identityState(m);
      if (!st.verified) errors.push(`${m.id}: identity verified requires ${st.missing.join(', ')}`);
    } else {
      for (const k of IDENTITY_FIELDS) if (a[k] !== null && a[k] !== undefined) errors.push(`${m.id}: amazon.${k} must be null while identityStatus is ${a.identityStatus}`);
    }
    if (a.offerStatus === 'verified') {
      const st = offerState(m);
      if (a.identityStatus !== 'verified') errors.push(`${m.id}: an offer cannot be verified before the product identity`);
      if (!st.verified) errors.push(`${m.id}: offer verified requires ${st.missing.join(', ')}`);
    } else {
      // pending/rejected offers carry no offer details: nothing may be assumed about seller, condition or stock
      for (const k of OFFER_FIELDS) if (a[k] !== null && a[k] !== undefined) errors.push(`${m.id}: amazon.${k} must be null while offerStatus is ${a.offerStatus}`);
    }
    if (a.observedConditions != null && (!Array.isArray(a.observedConditions) || a.observedConditions.some((c) => !['New', 'Used', 'Used - Very Good', 'Used - Like New', 'Renewed', 'Open Box'].includes(c)))) errors.push(`${m.id}: amazon.observedConditions must list known conditions`);
    if (typeof a.asin === 'string') {
      if (seenAsins.has(a.asin)) errors.push(`${m.id}: ASIN ${a.asin} is also used by ${seenAsins.get(a.asin)} (each model needs exactly one distinct ASIN)`);
      seenAsins.set(a.asin, m.id);
    }
    if (Array.isArray(m.catalogCandidateAsins) && a.asin && m.catalogCandidateAsins.includes(a.asin)) errors.push(`${m.id}: catalogCandidateAsins must list alternatives only, not the chosen ASIN`);
  }
  // Excluded models keep their evidence for later reconsideration but are never rendered, listed or linked.
  for (const x of guide.excludedModels || []) {
    if (ids.has(x.id)) errors.push(`excluded model ${x.id} is also listed`);
    if (!x.modelCode || !x.amazon) errors.push(`excluded model ${x.id}: modelCode and amazon evidence must be preserved`);
    if (!x.exclusion?.reason || !DATE_RE.test(x.exclusion?.decidedAt || '')) errors.push(`excluded model ${x.id}: exclusion.reason and decidedAt required`);
    if (typeof x.amazon?.asin === 'string') {
      if (seenAsins.has(x.amazon.asin)) errors.push(`excluded model ${x.id}: ASIN ${x.amazon.asin} is also used by ${seenAsins.get(x.amazon.asin)}`);
      seenAsins.set(x.amazon.asin, x.id);
    }
    if ((guide.quickPicks || []).some((p) => p.modelId === x.id)) errors.push(`excluded model ${x.id} appears in quick picks`);
  }
  for (const p of guide.quickPicks || []) if (!ids.has(p.modelId)) errors.push(`quickPick references unknown model ${p.modelId}`);
  if (!guide.disclosure || !/Amazon Associate/.test(guide.disclosure)) errors.push('Amazon Associates disclosure missing');
  if (!guide.research?.method?.length) errors.push('research method missing');
  // Internal verification records (amazon, catalogCandidateAsins) are never rendered, so they are not scanned for public-claim patterns.
  const publicView = { ...guide, sources: undefined, excludedModels: undefined, models: (guide.models || []).map(({ amazon, catalogCandidateAsins, ...rest }) => rest) };
  for (const [path, text] of strings(publicView)) {
    for (const sentence of text.split(/(?<=[.!?])\s+/)) {
      // A sentence that explicitly denies ratings ("no star ratings", "we do not repeat them") is allowed.
      const denies = /\b(no|not|without|never)\b/i.test(sentence);
      for (const [re, label] of FORBIDDEN_PATTERNS) {
        if (label === 'star rating' && denies) continue;
        if (re.test(sentence)) errors.push(`${path}: ${label} ("${sentence.slice(0, 50)}")`);
      }
    }
  }
  // Publication states: draft (preview only) -> approved (human sign-off recorded) -> published (public).
  if (!['draft', 'approved', 'published'].includes(guide.status)) errors.push('status must be draft, approved or published');
  if (guide.status === 'approved' || guide.status === 'published') {
    if (!guide.research?.reviewer) errors.push(`${guide.status} requires research.reviewer`);
    if (!DATE_RE.test(guide.approvedAt || '')) errors.push(`${guide.status} requires approvedAt (YYYY-MM-DD)`);
  }
  if (guide.status === 'draft' && (guide.approvedAt || guide.publishedAt)) errors.push('draft must not carry approvedAt/publishedAt');
  if (guide.status === 'published') {
    if (!DATE_RE.test(guide.publishedAt || '')) errors.push('published requires publishedAt (YYYY-MM-DD)');
    const verified = (guide.models || []).filter((m) => amazonLinkState(m).show).length;
    if ((guide.models || []).some((m) => !amazonLinkState(m).show)) errors.push('published requires every listed model to have verified identity and a verified New offer (remove models that failed)');
    if (verified < 3) errors.push('published requires at least 3 verified models');
  }
  return errors;
}

const money = (n) => n.toFixed(2);

/** Markdown rendering of the same fixture (used to keep docs/editorial-mvp/07_ARTICLE_DRAFT.md in sync). */
export function renderGuideMarkdown(guide) {
  const L = [];
  L.push(`# ${guide.title}`, '', `> **${guide.status.toUpperCase()} — not approved for indexing (indexing is controlled separately by seo/sitemap-manifest.json).** Generated from \`src/content/editorial/dellSmallDesks.json\` (\`node scripts/editorial-to-markdown.mjs\`). Research date ${guide.research.date}; editorial reviewer: ${guide.research.reviewer || 'pending'}.`, '');
  L.push(`*${guide.disclosure}*`, '', guide.summary, '');
  L.push('## How we researched this', '', ...guide.research.method.map((x) => `- ${x}`), '');
  for (const p of guide.intro) L.push(p, '');
  if (guide.verdict) L.push(`## ${guide.verdict.heading}`, '', ...guide.verdict.paragraphs.flatMap((p) => [p, '']));
  L.push('## Quick picks', '', '| Pick | Model | Why |', '|---|---|---|');
  for (const p of guide.quickPicks) L.push(`| ${p.role} | ${guide.models.find((m) => m.id === p.modelId).name} | ${p.why} |`);
  L.push('', '## Desk footprint and stand', '', '| Model | Width × depth (in) | Calculated footprint | Height (in) | Stand adjustment |', '|---|---|---|---|---|');
  for (const m of guide.models) {
    const f = m.footprint;
    const h = f.heightMinIn === f.heightMaxIn ? `${f.heightMinIn}` : `${f.heightMinIn}–${f.heightMaxIn}`;
    L.push(`| ${m.name} | ${f.widthIn} × ${f.depthIn} | ≈${footprintArea(m)} sq in | ${h} | Height: ${m.stand.heightAdjust}; tilt ${m.stand.tilt}; swivel ${m.stand.swivel}; pivot ${m.stand.pivot} |`);
  }
  L.push('', `*Width, depth and height are Dell's published dimensions with the stand; footprint is width × depth, calculated by us. Stand depth differs by only ${depthSpread(guide.models)} in across the ${countWord(guide.models.length)}.*`, '');
  L.push('## Screen and connections', '', '| Model | Screen | Video inputs | USB | USB-C | Speakers | VESA | Warranty (Dell US) |', '|---|---|---|---|---|---|---|---|');
  for (const m of guide.models) L.push(`| ${m.name} | ${m.display} | ${m.videoInputs} | ${m.usb} | ${m.usbC} | ${m.speakers} | ${m.vesa} | ${m.warrantyUs} |`);
  L.push('');
  for (const m of guide.models) {
    L.push(`## ${m.name}`, '', `**${m.role}.** Best for: ${m.bestFor} Not for: ${m.notFor}`, '', `Panel: ${m.panelNotes}. Weight: ${m.weight}.`, '', '**Strengths**', ...m.strengths.map((x) => `- ${x}`), '', '**Compromises**', ...m.compromises.map((x) => `- ${x}`), '', ...(m.listingNote ? [`> ${m.listingNote}`, ''] : []), `Source: [Dell US product page](${m.dell.url}) (read ${m.dell.retrieved}). Amazon: product identity ${identityState(m).verified ? 'verified' : 'pending'}; offer ${offerState(m).verified ? 'verified' : 'pending verification'}${amazonLinkState(m).show ? '' : ' (no link shown)'}.`, '');
  }
  for (const g of guide.guidance) L.push(`## ${g.heading}`, '', ...g.paragraphs.flatMap((p) => [p, '']));
  L.push('## FAQ', '');
  for (const f of guide.faq) L.push(`**${f.q}**`, '', f.a, '');
  L.push('## Sources', '', ...guide.sources.map((s) => `- [${s.label}](${s.url}) — read ${s.retrieved}`), '');
  return L.join('\n');
}
